import React from 'react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from './ui/accordion';
import { HelpCircle } from 'lucide-react';

const FAQSection = () => {
  const faqs = [
    {
      question: 'What is a trademark and why do I need one?',
      answer: 'A trademark is a unique symbol, word, or phrase that identifies your brand and distinguishes it from competitors. Registering a trademark gives you exclusive legal rights to use that mark in connection with your goods or services, protects your brand identity, and helps prevent others from using similar marks that could confuse customers.'
    },
    {
      question: 'How long does the trademark registration process take?',
      answer: 'The entire trademark registration process typically takes 8-12 months from filing to registration. However, we process and file your application within 24 hours of receiving your information. The USPTO review process can take several months, and we monitor your application throughout to handle any issues that arise.'
    },
    {
      question: 'What is included in your trademark registration packages?',
      answer: 'Our packages include comprehensive trademark search, professional application preparation, filing with the USPTO, monitoring of your application status, and dedicated customer support. Higher-tier packages include additional services like cease and desist letters, direct hit searches, dedicated case managers, and extended monitoring periods.'
    },
    {
      question: 'How much does trademark registration cost?',
      answer: 'Our trademark registration services start at just $45 for the Basic package. This fee covers our professional services. Please note that USPTO government filing fees (currently $250-$350 per class) are separate and paid directly to the government. We offer four package tiers to suit different needs and budgets.'
    },
    {
      question: 'What happens if my trademark application is rejected?',
      answer: 'If the USPTO issues an office action or rejection, we will notify you immediately and work with you to address the concerns. Our Premium and Elite packages include support for handling office actions. In most cases, rejections can be overcome with proper responses and amendments to your application.'
    },
    {
      question: 'Can I trademark a business name or logo?',
      answer: 'Yes, you can trademark both business names and logos. In fact, many businesses choose to register both for comprehensive protection. A word mark protects your business name in any font or style, while a design mark protects your specific logo design. We can help you determine the best approach for your brand.'
    },
    {
      question: 'Do I need a trademark if I already have a business license?',
      answer: 'Yes, a business license and trademark serve different purposes. A business license allows you to operate legally in your jurisdiction, while a trademark protects your brand name, logo, and identity nationwide. Without a trademark, another company could legally use a similar name in another state or industry.'
    },
    {
      question: 'What is the difference between a trademark and a copyright?',
      answer: 'A trademark protects brand names, logos, and slogans used to identify goods or services. A copyright protects original creative works like books, music, art, and software. Trademarks can last indefinitely with proper maintenance, while copyrights last for the creator\'s lifetime plus 70 years. Most businesses need both types of protection.'
    },
    {
      question: 'Can I file a trademark myself or do I need professional help?',
      answer: 'While you can file a trademark yourself, professional assistance significantly increases your chances of success. Our experienced team conducts proper trademark searches, prepares applications correctly, selects appropriate classifications, and handles complex USPTO requirements. This expertise helps avoid costly mistakes and delays.'
    },
    {
      question: 'What is Amazon Brand Registry and do I need it?',
      answer: 'Amazon Brand Registry is a program that helps protect your brand on Amazon by giving you greater control over your product listings and enhanced tools to combat counterfeits. If you sell products on Amazon, enrolling in Brand Registry provides powerful benefits including A+ Content, Stores, and brand protection tools. A registered trademark is required to enroll.'
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-emerald-50 via-teal-50 to-white">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-brand-gradient rounded-full mb-4">
              <HelpCircle className="text-white" size={32} />
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">
              Frequently Asked{' '}
              <span className="bg-brand-gradient bg-clip-text text-transparent">
                Questions
              </span>
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Get answers to common questions about trademark registration and our services
            </p>
          </div>

          {/* FAQ Accordion */}
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <Accordion type="single" collapsible className="w-full">
              {faqs.map((faq, index) => (
                <AccordionItem key={index} value={`item-${index}`} className="border-b border-gray-200 last:border-0">
                  <AccordionTrigger className="text-left hover:text-brand-primary transition-colors py-5">
                    <span className="font-semibold text-lg text-gray-800 pr-4">
                      {faq.question}
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="text-base text-gray-600 leading-relaxed pb-5">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>

          {/* CTA */}
          <div className="text-center mt-12">
            <p className="text-lg text-gray-600 mb-4">
              Still have questions? We're here to help!
            </p>
            <a
              href="#home"
              className="inline-flex items-center text-brand-primary hover:text-emerald-700 font-semibold text-lg group"
            >
              Contact our support team
              <svg
                className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 8l4 4m0 0l-4 4m4-4H3"
                />
              </svg>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
